import * as React from "react";
import { FlagTag, Tag as TagModel } from "@models/tag";
import { Divider, Modal, Table, Button } from "antd";
import { FormattedMessage, injectIntl, intlShape } from "react-intl";
import FormattedDateTime from "@components/custom/FormattedDateTime";
import Message from "@constants/message";
import { ucFirst } from "@utils";
import { PaginationDefaultParams } from "../custom/Paginator";
import TextWithTooltip from "@components/custom/TextWithTooltip";
import { NavLink } from "react-router-dom";

type Props = {
  data: TagModel[];
  loading: boolean;
  deleteFn: (item: TagModel) => void;
  intl: intlShape.isRequired;
  currentPage: number;
};

const TagsTable: React.FC<Props> = props => {
  const { data, loading, currentPage, deleteFn, intl } = props;
  const { formatMessage } = intl;
  const pageOffset = (currentPage - 1) * PaginationDefaultParams.count;
  
  const deleteTag = (item: TagModel): void => {
    Modal.confirm({
      content: formatMessage({
        id: "app.general_delete_warning",
        defaultMessage: Message.Warning.Delete,
      }),
      okText: formatMessage({
        id: "app.delete",
        defaultMessage: "Delete",
      }),
      cancelText: formatMessage({
        id: "app.cancel",
        defaultMessage: "Cancel",
      }),
      className: "delete-confirm-dialog",
      onOk: () => deleteFn(item),
    });
  };

  const columns = [
    {
      title: <FormattedMessage id="app.index" defaultMessage={`Index`} />,
      width: 30,
      dataIndex: "index",
      key: "id",
      render: (a, b, i) => <span>{i + 1 + pageOffset}</span>
    },
    {
      title: <FormattedMessage id="app.title" defaultMessage={`Title`} />,
      width: 150,
      dataIndex: "title",
      key: "title"
    },
    {
      title: <FormattedMessage id="app.slug" defaultMessage={`Slug`} />,
      width: 100,
      dataIndex: "slug",
      key: "slug"
    },
    {
      title: <FormattedMessage id="app.status" defaultMessage={`Status`} />,
      width: 100,
      dataIndex: "flag",
      key: "flag",
      render: flag => {
        return flag == FlagTag.NONE ? null : (
          <FormattedMessage id={`app.${flag}`} defaultMessage={ucFirst(flag)} />
        );
      }
    },
    {
      title: <FormattedMessage id="app.body" defaultMessage={`Description`} />,
      width: 200,
      dataIndex: "body",
      key: "body",
      render: text => <TextWithTooltip text={text} />
    },
    {
      title: (
        <FormattedMessage
          id="app.creation_date"
          defaultMessage={`Creation Date`}
        />
      ),
      width: 120,
      dataIndex: "created_at",
      key: "created_at",
      render: (text, record) => <FormattedDateTime date={text} />
    },
    {
      title: "",
      key: "actions",
      width: 120,
      dataIndex: "actions",
      render: (text, record) => (
        <span>
          <NavLink to={`/tags/${record.id}/edit`}>
            <Button type="link" size={`small`}>
              <FormattedMessage id="app.edit" defaultMessage={`Edit`} />
            </Button>
          </NavLink>

          <Divider type="vertical" size={`small`} />

          <Button
            type="link"
            className={`danger`}
            onClick={() => deleteTag(record)}
          >
            <FormattedMessage id="app.delete" defaultMessage={`Delete`} />
          </Button>
        </span>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      loading={loading}
      pagination={false}
      dataSource={data}
      rowKey={record => record.id}
    />
  );
};

export default injectIntl(TagsTable);
